import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { features } from '../mock';
import { ArrowUpRight } from 'lucide-react';
import { RevealHeading } from './anim/SectionReveal';

const FeatureRow = ({ f, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 1.05]);
  const rotate = useTransform(scrollYProgress, [0, 1], [i % 2 === 0 ? -2 : 2, i % 2 === 0 ? 2 : -2]);
  const textX = useTransform(scrollYProgress, [0, 0.5, 1], [i % 2 === 0 ? 40 : -40, 0, i % 2 === 0 ? -20 : 20]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9 }}
      className={`grid md:grid-cols-12 items-center gap-8 md:gap-16 ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`}
    >
      <motion.div style={{ y, rotate }} className="md:col-span-7 relative parallax-slow">
        <motion.div
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute -inset-6 border border-[#c8942a]/40 pointer-events-none origin-top"
        />
        <div className="relative overflow-hidden">
          <motion.img
            style={{ scale: imgScale }}
            src={f.img}
            alt={f.title}
            className="relative w-full aspect-[4/3] object-cover shadow-2xl"
          />
          <motion.div
            initial={{ x: '-100%' }}
            whileInView={{ x: '100%' }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.65, 0, 0.35, 1] }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="absolute -bottom-4 left-6 bg-white shadow-md px-4 py-2 border-l-2 border-[#c8942a]"
        >
          <span className="font-sans-pro text-[10px] uppercase tracking-[0.28em] text-[#8f6417]">{f.accent}</span>
        </motion.div>
      </motion.div>

      <motion.div style={{ x: textX }} className="md:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="font-sans-pro text-[11px] uppercase tracking-[0.4em] text-[#c8942a] font-semibold"
        >
          0{i + 1} — / — 04
        </motion.div>
        <RevealHeading
          text={f.title.charAt(0) + f.title.slice(1).toLowerCase()}
          className="mt-4 font-serif-display text-3xl md:text-5xl font-black text-[#1c1a17] leading-[1.1]"
        />
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.7 }}
          className="h-px w-16 bg-[#c8942a] my-6 origin-left"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="text-[#4a4238] leading-[1.75] text-[15px] font-light"
        >
          {f.desc}
        </motion.p>
        <motion.a
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.55 }}
          href="#about"
          className="mt-8 inline-flex items-center gap-2 font-sans-pro text-[11px] uppercase tracking-[0.28em] text-[#1c1a17] hover:text-[#b8331f] transition-colors group"
        >
          Discover more <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </motion.a>
      </motion.div>
    </motion.div>
  );
};

const Features = () => {
  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#fbf7f2]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="text-center mb-20 max-w-3xl mx-auto"
        >
          <div className="tag-label justify-center">Our School</div>
          <RevealHeading
            text="A Cambridge Pathway education, rooted in character"
            highlight="rooted"
            className="font-serif-display text-4xl md:text-6xl font-black mt-5 text-[#1c1a17] leading-[1.05]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="mt-6 text-[#4a4238] text-lg font-light leading-relaxed"
          >
            We deliver the English National Curriculum — from Pre-Nursery, via Cambridge Checkpoint,
            through to IGCSE and A-Level — within a community that champions goodness, discipline and
            personal excellence.
          </motion.p>
        </motion.div>

        <div className="space-y-24 md:space-y-32">
          {features.map((f, i) => (
            <FeatureRow key={f.title} f={f} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
